import { useEffect, useRef, useState } from "react";
import { ImagePlus, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/adminApi";
import { VariantEditor, blankVariant, toPayload } from "@/AdminVariants";

const EMPTY = () => ({ brand: "", name: "", description: "", type: "", image: "", featured: false, status: "published", variants: [blankVariant()] });
export function PhotoUpload({ http, value, onChange, testId }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await http.post("/admin/uploads", fd);
      onChange(r.data.url);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <button type="button" className="photo-upload" onClick={() => ref.current.click()} disabled={busy} data-testid={testId}>
      {value ? <img src={value} alt="" /> : <ImagePlus size={20} />}
      <span>{busy ? "Uploading…" : value ? "Change photo" : "Upload photo"}</span>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={pick} data-testid={`${testId}-input`} />
    </button>
  );
}

export function AddProductForm({ http, onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.image) return toast.error("Please upload a product photo");
    setSaving(true);
    try {
      const { type, ...rest } = f;
      const r = await http.post("/admin/products", { ...rest, specs: type ? { Type: type } : {}, variants: toPayload(f.variants) });
      onCreated(r.data);
      toast.success(`${r.data.name} added with ${r.data.variants.length} size(s)`);
      setF(EMPTY());
      setOpen(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };
  if (!open)
    return (
      <button className="btn btn-red" onClick={() => setOpen(true)} data-testid="admin-add-product-open">
        <Plus size={15} /> Add tyre
      </button>
    );
  return (
    <form className="admin-form" onSubmit={submit} data-testid="admin-add-product-form">
      <div className="admin-form-head">
        <b>New tyre</b>
        <button type="button" onClick={() => setOpen(false)} data-testid="admin-add-product-close">
          <X size={16} />
        </button>
      </div>
      <PhotoUpload http={http} value={f.image} onChange={(image) => setF({ ...f, image })} testId="new-product-photo" />
      <div className="admin-form-grid">
        <label>Brand<input required value={f.brand} onChange={set("brand")} data-testid="new-product-brand" /></label>
        <label>Model name<input required value={f.name} onChange={set("name")} data-testid="new-product-name" /></label>
        <label>Type<input value={f.type} onChange={set("type")} placeholder="Ultra-high performance" data-testid="new-product-spec-type" /></label>
        <label className="wide">Description<textarea value={f.description} onChange={set("description")} data-testid="new-product-description" /></label>
        <div className="wide">
          <p className="admin-form-label">Sizes, prices & stock</p>
          <VariantEditor value={f.variants} onChange={(variants) => setF({ ...f, variants })} testId="new-product-variants" />
        </div>
        <label>Status<select value={f.status} onChange={set("status")} data-testid="new-product-status"><option value="published">Published</option><option value="unpublished">Unpublished (draft)</option></select></label>
        <label className="admin-toggle"><input type="checkbox" checked={f.featured} onChange={set("featured")} data-testid="new-product-featured" />Feature on homepage</label>
      </div>
      <button className="btn btn-red" disabled={saving} data-testid="new-product-submit">{saving ? "Adding…" : "Add to store"}</button>
    </form>
  );
}

export function SettingsPanel({ http }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const pick = (d) => ({ usd_inr_rate: d.usd_inr_rate, shipping_inr: d.shipping_inr, enquiry_email: d.enquiry_email || "", rate_updated_at: d.rate_updated_at });
  useEffect(() => {
    http.get("/admin/settings").then((r) => setS(pick(r.data))).catch(() => {});
  }, [http]);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await http.put("/admin/settings", { usd_inr_rate: Number(s.usd_inr_rate), shipping_inr: Number(s.shipping_inr), enquiry_email: s.enquiry_email || null });
      setS(pick(r.data));
      toast.success("Store settings saved");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };
  if (!s) return null;
  const rate = Number(s.usd_inr_rate || 0);
  return (
    <section className="admin-section" data-testid="admin-settings">
      <h2>Settings <i>store</i></h2>
      <form className="admin-settings" onSubmit={save}>
        <label>USD to INR rate<input required type="number" min="0.01" step="0.01" value={s.usd_inr_rate} onChange={(e) => setS({ ...s, usd_inr_rate: e.target.value })} data-testid="settings-rate" /></label>
        <label>Shipping fee (₹)<input required type="number" min="0" step="1" value={s.shipping_inr} onChange={(e) => setS({ ...s, shipping_inr: e.target.value })} data-testid="settings-shipping" /></label>
        <label>Business notification email<input type="email" value={s.enquiry_email} onChange={(e) => setS({ ...s, enquiry_email: e.target.value })} placeholder="you@business.com" data-testid="settings-enquiry-email" /></label>
        <button className="btn btn-red" disabled={saving} data-testid="settings-save">{saving ? "Saving…" : "Save settings"}</button>
        <p data-testid="settings-preview">
          A $100 tyre shows as approx. ₹{Math.round(100 * rate).toLocaleString("en-IN")}; shipping ₹{Number(s.shipping_inr || 0).toLocaleString("en-IN")} ≈ ${rate ? (Number(s.shipping_inr || 0) / rate).toFixed(2) : "—"}.
          {" "}Rate last updated {s.rate_updated_at ? new Date(s.rate_updated_at).toLocaleString() : "— not recorded"}.
        </p>
      </form>
    </section>
  );
}
